// Copyright: IQGeo Limited 2010-2019
import myw from 'myWorld/base/core';
import L from 'leaflet';
import _ from 'underscore';
import 'jquery';
import 'myWorld/layers/canvasRendererHelper';
import 'myWorld/layers/tileBounds';

myw.ClippedTileLayer = L.TileLayer.extend({
    /**
     * @class  A tile layer which supports clipping by a multi-polygon
     * @constructs
     * @param  {string} url   URL for the tile server
     * @param  {object} options   Options
     * Extends {@link http://leafletjs.com/reference.html#tilelayer|L.TileLayer}
     * Options in addition to those of L.TileLayer:
     *  clipGeometries: {polygonGeometry[]}  The GeoJSON geometries to clip by
     * The geometries should be Polygon or MultiPolygon geometries.
     * Each polygon should have linear rings which don't self-intersect and
     * which don't intersect each other. The external ring should be oriented
     * anti-clockwise. The internal rings should be oriented clockwise and lie
     * inside the external ring.
     * It does not matter if different polygons in the list of geometries
     * intersect.
     */
    initialize(url, options) {
        this.setClipGeometry(options.clipGeometries || []);
        L.TileLayer.prototype.initialize.call(this, url, options);
    },

    createTile(coords) {
        // create a <canvas> element for drawing
        const tile = L.DomUtil.create('canvas', 'leaflet-tile');
        // setup tile width and height according to the options
        const size = this.getTileSize();
        tile.width = size.x;
        tile.height = size.y;

        // return the tile so it can be rendered on screen
        this.drawTile(tile, coords, coords.z);
        return tile;
    },

    /**
     * Draw a tile on a canvas
     * Subclasses L.TileLayer.Canvas method
     * @param {canvas} canvas       HTML canvas
     * @param {L.Point} tilePoint   Tile point
     * @param {integer} zoom        Zoom level
     */
    drawTile(canvas, tilePoint, zoom) {
        if (this._clipGeometry.length > 0) {
            this._drawClippedTile(canvas, tilePoint, zoom);
        } else {
            this._drawUnclippedTile(canvas, tilePoint, zoom);
        }
    },

    _drawClippedTile(canvas, tilePoint, zoom) {
        const clipGeometry = this._clipGeometryAtZoom(zoom);
        const tileBounds = new myw.TileBounds(tilePoint, this.options.tileSize);
        const clippedGeometry = tileBounds.clipByMultiPolygon(clipGeometry);

        if (clippedGeometry.length > 0) {
            // ENH: If the clippedGeometry is just the whole tile, we don't need to
            // clip at all. However, the cost of checking may outweigh any benefits
            // of not having to set up the clipping path on the canvas...
            const boundsMin = tileBounds.getMin();
            const adjustedGeom = this._newMultiPolygonCoordsApplying(
                coordinate => coordinate.subtract(boundsMin),
                clippedGeometry
            );
            tilePoint.z = zoom; // Need to set z to get URL
            const url = this.getTileUrl(tilePoint);
            const img = new Image();
            img.onload = () => {
                const helper = new myw.CanvasRendererHelper(canvas);
                helper.drawClippedImageAtOrigin(adjustedGeom, img);
            };
            img.src = url;
        }
    },

    _drawUnclippedTile(canvas, tilePoint, zoom) {
        tilePoint.z = zoom; // Need to set z to get URL
        const url = this.getTileUrl(tilePoint);
        const img = new Image();
        img.onload = () => {
            const helper = new myw.CanvasRendererHelper(canvas);
            helper.drawImageAtOrigin(img);
        };
        img.src = url;
    },

    /**
     * @param  {polygonGeometry[]} geometries  GeoJSON geometries to clip by
     * The geometries should be Polygon or MultiPolygon geometries.
     * Each polygon should have linear rings which don't self-intersect and
     * which don't intersect each other. The external ring should be oriented
     * anti-clockwise. The internal rings should be oriented clockwise and lie
     * inside the external ring.
     * It does not matter if different polygons in the list of geometries
     * intersect.
     */
    setClipGeometry(geometries) {
        const multiPolygons = _.map(geometries, this._normalizeGeometry);
        this._clipGeometry = [].concat(...multiPolygons);
        this._clipGeometryCache = [];
    },

    /*
     * Return the coordinates of the clip geometry projected into pixel space
     * at the specified zoom level. Results are cached so repeated calls for a
     * given zoom level doesn't require recalculation.
     * @param {integer} zoom   Zoom level
     * @return {MultiPolygon}  Nested array of coordinates representing a multipolygon
     */
    _clipGeometryAtZoom(zoom) {
        if (!this._clipGeometryCache[zoom]) {
            this._clipGeometryCache[zoom] = this._getGeometry(zoom);
        }
        return this._clipGeometryCache[zoom];
    },

    /*
     * Project the clip geometry into pixel space at the specified zoom level.
     * @param {integer} zoom   Zoom level
     * @return {MultiPolygon}  Nested array of coordinates representing a multipolygon
     */
    _getGeometry(zoom) {
        const map = this._map;
        return this._newMultiPolygonCoordsApplying(
            coordinate => map.project([coordinate[1], coordinate[0]], zoom),
            this._clipGeometry
        );
    },

    /*
     * Apply function to all coordinates in a multi-polygon coordinate array
     * @param {function} func  Function to apply
     * @param {MultiPolygon} multiPolyCoordinates  Multi-polygon coodinates
     * @return {MultiPolygon}  The new multi-polygon
     */
    _newMultiPolygonCoordsApplying(func, multiPolyCoordinates) {
        const newLineStringCoordsApplying = (func, coordinates) => _.map(coordinates, func);
        const newPolygonCoordsApplying = (func, polyCoordinates) =>
            _.map(polyCoordinates, newLineStringCoordsApplying.bind(null, func));
        return _.map(multiPolyCoordinates, newPolygonCoordsApplying.bind(null, func));
    },

    /*
     * Returns a nested array of coordinates representing the given geoemtry as a
     * multipolygon.
     * @param {polygonGeometry} geometry   A Polygon or Multipolygon GeoJSON geometry
     * @return {MultiPolygon}  Nested array of coordinates representing a multipolygon
     */
    _normalizeGeometry(geometry) {
        if (geometry.type === 'Polygon') {
            return [geometry.coordinates];
        } else if (geometry.type === 'MultiPolygon') {
            return geometry.coordinates;
        } else {
            throw new Error(`Unsupported type: ${geometry.type}`);
        }
    }
});

export default myw.ClippedTileLayer;
